import React, { useRef, useState } from 'react';
import axiosInstance from '../axios/axiosInstance';

const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || '');
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(new Error('Could not read file.'));
        reader.readAsDataURL(file);
    });

/**
 * URL field + file upload for firm branding assets.
 * kind: 'logo' | 'loginLogo' | 'favicon'
 * Uses JSON base64 upload (same CORS path as other API calls).
 */
const BrandingImageField = ({
    label,
    kind,
    value,
    onChange,
    placeholder = 'https://...',
}) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setError('');
        if (file.size > 2 * 1024 * 1024) {
            setError('File too large. Max 2 MB.');
            return;
        }
        setUploading(true);
        try {
            const contentBase64 = await readFileAsBase64(file);
            const res = await axiosInstance.post('/firms/upload-branding', {
                kind,
                fileName: file.name,
                contentBase64,
            });
            const url = res.data?.url;
            if (!url) throw new Error('No URL returned');
            onChange(url);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                (err?.response?.status ? `Upload failed (${err.response.status}).` : null) ||
                err?.message ||
                'Upload failed.';
            setError(msg);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>
                {label}
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {value ? (
                    <img
                        src={value}
                        alt=""
                        style={{
                            width: 40, height: 40, borderRadius: 6, objectFit: 'contain',
                            border: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0,
                        }}
                    />
                ) : (
                    <div style={{
                        width: 40, height: 40, borderRadius: 6, background: '#f1f5f9',
                        border: '1px dashed #cbd5e1', flexShrink: 0,
                    }} />
                )}
                <input
                    type="text"
                    value={value || ''}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    style={{
                        flex: 1, padding: '8px 10px', borderRadius: 7,
                        border: '1px solid #d1d5db', fontSize: 13,
                        outline: 'none', boxSizing: 'border-box',
                    }}
                />
                <button
                    type="button"
                    disabled={uploading}
                    onClick={() => inputRef.current?.click()}
                    style={{
                        flexShrink: 0, padding: '8px 12px', borderRadius: 7,
                        border: '1px solid #d1d5db', background: '#fff',
                        fontSize: 12, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer',
                        color: '#334155',
                    }}
                >
                    {uploading ? 'Uploading…' : 'Upload'}
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,image/x-icon,.ico"
                    style={{ display: 'none' }}
                    onChange={handleUpload}
                />
            </div>
            {error && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{error}</div>}
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Upload image (max 2 MB) or paste a URL
            </div>
        </div>
    );
};

export default BrandingImageField;
